import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { buildTaxExtractionPrompt } from '../../../lib/ocr/tax-prompt';

/**
 * S10: OCR Integration Pipeline
 * POST /api/ocr/process
 * 
 * Processes uploaded documents through AI-powered OCR.
 * Uses OpenAI GPT-4 Vision for document understanding and data extraction.
 * 
 * Body: { documentId: string, forceReprocess?: boolean }
 * 
 * GDPR Art. 22: Users are informed about automated AI processing
 * in the privacy policy (Section 4).
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  const { documentId, forceReprocess } = req.body;

  if (!documentId) {
    return res.status(400).json({ error: 'documentId es requerido.' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();

    // 1. Get document metadata
    const { data: doc, error: docError } = await supabaseAdmin
      .from('documentos')
      .select('id, nombre, tipo, estado, url_archivo, cliente_id, ocr_status, ocr_result')
      .eq('id', documentId)
      .single();

    if (docError || !doc) {
      return res.status(404).json({ error: 'Documento no encontrado.' });
    }

    // Skip if already processed (unless forced)
    if (doc.ocr_status === 'completed' && !forceReprocess) {
      return res.status(200).json({
        success: true,
        status: 'already_processed',
        data: doc.ocr_result,
      });
    }

    // 2. Mark as processing
    await supabaseAdmin
      .from('documentos')
      .update({ ocr_status: 'processing' })
      .eq('id', documentId);

    // 3. Download document from storage
    // Extract path from full URL (remove base URL prefix)
    const storagePath = doc.url_archivo.replace(/^https:\/\/[^/]+\/storage\/v1\/object\/public\/documentos\//, '');
    const { data: fileData, error: fileError } = await supabaseAdmin.storage
      .from('documentos')
      .download(storagePath);

    if (fileError || !fileData) {
      await supabaseAdmin
        .from('documentos')
        .update({ ocr_status: 'failed', ocr_error: 'No se pudo descargar el archivo.' })
        .eq('id', documentId);
      return res.status(500).json({ error: 'Error descargando documento.' });
    }

    // 4. Convert to base64 for API
    const buffer = Buffer.from(await fileData.arrayBuffer());
    let base64 = buffer.toString('base64');
    let mimeType = fileData.type || 'application/pdf';

    // GPT-4 Vision only supports images, not PDFs
    // For PDFs, we need to convert to image first or use a different approach
    const isPdf = mimeType === 'application/pdf' || doc.nombre.toLowerCase().endsWith('.pdf');
    
    if (isPdf) {
      // For PDFs, try using pdf-poppler or similar to convert to image
      // For now, we'll use a workaround: extract text using a simpler method
      // and fall back to returning an error asking for image upload
      try {
        // Try dynamic import of pdf-parse for text extraction
        const { PDFParse } = await import('pdf-parse');
        const pdf = new PDFParse({ data: buffer });
        const pdfText = await pdf.getText();
        pdf.destroy();
        const pdfData = { text: pdfText };
        
        // Use comprehensive tax extraction prompt
        const openaiKey = process.env.OPENAI_API_KEY;
        const textResponse = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${openaiKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'gpt-4o',
            messages: [
              { role: 'system', content: buildTaxExtractionPrompt() },
              { role: 'user', content: `TEXTO DEL DOCUMENTO:\n${pdfData.text}` }
            ],
            max_tokens: 4000,
            temperature: 0.1,
          }),
        });

        if (!textResponse.ok) {
          throw new Error(`OpenAI error: ${textResponse.status}`);
        }

        const aiResult = await textResponse.json();
        const content = aiResult.choices?.[0]?.message?.content || '';
        
        let extractedData: any;
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        extractedData = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(content);

        await supabaseAdmin
          .from('documentos')
          .update({
            ocr_status: 'completed',
            ocr_result: extractedData,
            ocr_error: null,
            ocr_processed_at: new Date().toISOString(),
          })
          .eq('id', documentId);

        return res.status(200).json({
          success: true,
          status: 'completed',
          method: 'pdf_text_extraction',
          data: extractedData,
          tokens_used: aiResult.usage?.total_tokens,
        });
      } catch (pdfError: any) {
        console.error('[OCR] PDF processing error:', pdfError);
        // Fall through to try vision API with error
      }
    }

    // 5. Call OpenAI GPT-4 Vision for extraction (images only)
    const openaiKey = process.env.OPENAI_API_KEY;
    if (!openaiKey) {
      await supabaseAdmin
        .from('documentos')
        .update({ ocr_status: 'failed', ocr_error: 'OPENAI_API_KEY no configurada.' })
        .eq('id', documentId);
      return res.status(500).json({ error: 'OPENAI_API_KEY no configurada.' });
    }

    const openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${openaiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: buildTaxExtractionPrompt() },
          {
            role: 'user',
            content: [
              { type: 'text', text: 'Analiza este documento financiero/contable y extrae todos los datos fiscales según las instrucciones.' },
              {
                type: 'image_url',
                image_url: {
                  url: `data:${mimeType};base64,${base64}`,
                  detail: 'high',
                },
              },
            ],
          },
        ],
        max_tokens: 4000,
        temperature: 0.1,
      }),
    });

    if (!openaiResponse.ok) {
      const errText = await openaiResponse.text();
      await supabaseAdmin
        .from('documentos')
        .update({ ocr_status: 'failed', ocr_error: `OpenAI API error: ${openaiResponse.status}` })
        .eq('id', documentId);
      console.error('[OCR] OpenAI error:', errText);
      return res.status(500).json({ error: 'Error en procesamiento IA.' });
    }

    const aiResult = await openaiResponse.json();
    const content = aiResult.choices?.[0]?.message?.content || '';

    // 6. Parse JSON from response
    let extractedData: any;
    try {
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      extractedData = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(content);
    } catch (parseError) {
      await supabaseAdmin
        .from('documentos')
        .update({
          ocr_status: 'failed',
          ocr_error: 'No se pudo parsear la respuesta de la IA.',
          ocr_result: { raw_response: content },
        })
        .eq('id', documentId);
      return res.status(500).json({ error: 'Error parseando resultado IA.' });
    }

    // 7. Save result
    await supabaseAdmin
      .from('documentos')
      .update({
        ocr_status: 'completed',
        ocr_result: extractedData,
        ocr_error: null,
        ocr_processed_at: new Date().toISOString(),
      })
      .eq('id', documentId);

    // 8. Log AI processing for GDPR (Art. 22 transparency)
    try {
      await supabaseAdmin.from('metricas_ia').insert({
        tipo_operacion: 'ocr_extraction',
        modelo_usado: 'gpt-4o',
        documento_id: documentId,
        cliente_id: doc.cliente_id,
        usuario_id: user.id,
        confianza: extractedData.confianza || null,
        tokens_usados: aiResult.usage?.total_tokens || null,
        resultado: 'success',
      });
    } catch (_) { /* Non-blocking */ }

    return res.status(200).json({
      success: true,
      status: 'completed',
      data: extractedData,
      tokens_used: aiResult.usage?.total_tokens,
    });
  } catch (error: any) {
    console.error('[OCR] Error:', error);
    return res.status(500).json({ error: 'Error procesando documento.' });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
